'use client';

import React, { Ref } from 'react';

// third-party
import { motion, useCycle } from 'framer-motion';

type ScaleProps = {
  hover: number | string | undefined;
  tap: number | string | undefined;
};

interface AnimateButtonProps {
  children?: React.ReactNode;
  type?: 'slide' | 'scale' | 'rotate' | 'pulse' | 'bounce' | 'glow';
  direction?: 'up' | 'down' | 'left' | 'right';
  offset?: number;
  scale?: ScaleProps;
  pulse?: boolean;
}

// ==============================|| ANIMATION BUTTON ||============================== //

const AnimateButton = React.forwardRef(
  (
    {
      children,
      type = 'scale',
      direction = 'right',
      offset = 10,
      scale = {
        hover: 1,
        tap: 0.9
      },
      pulse = false
    }: AnimateButtonProps,
    ref: Ref<HTMLDivElement>
  ) => {
    let offset1;
    let offset2;
    switch (direction) {
      case 'up':
      case 'left':
        offset1 = offset;
        offset2 = 0;
        break;
      case 'right':
      case 'down':
      default:
        offset1 = 0;
        offset2 = offset;
        break;
    }

    const [x, cycleX] = useCycle(offset1, offset2);
    const [y, cycleY] = useCycle(offset1, offset2);

    switch (type) {
      case 'rotate':
        return (
          <motion.div
            ref={ref}
            animate={{ rotate: 360 }}
            transition={{
              repeat: Infinity,
              repeatType: 'loop',
              duration: 2,
              repeatDelay: 0
            }}
          >
            {children}
          </motion.div>
        );
      case 'bounce':
        return (
          <motion.div
            ref={ref}
            animate={{ y: [0, -4, 0], scale: [1, 1, 1] }}
            transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ 
              scale: 1.1,
              y: 0,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            {children}
          </motion.div>
        );
      case 'glow':
        return (
          <motion.div
            ref={ref}
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(0,0,0,0)",
                "0 0 0 10px rgba(0,0,0,0)",
                "0 0 0 0 rgba(0,0,0,0)"
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            whileHover={{ 
              scale: 1.1,
              transition: { type: "spring", stiffness: 400, damping: 10 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            {children}
          </motion.div>
        );
      case 'slide':
        if (direction === 'up' || direction === 'down') {
          return (
            <motion.div ref={ref} animate={{ y: y !== undefined ? y : '' }} onHoverEnd={() => cycleY()} onHoverStart={() => cycleY()}>
              {children}
            </motion.div>
          );
        }
        return (
          <motion.div ref={ref} animate={{ x: x !== undefined ? x : '' }} onHoverEnd={() => cycleX()} onHoverStart={() => cycleX()}>
            {children}
          </motion.div>
        );

      case 'pulse':
        return (
          <motion.div
            ref={ref}
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {children}
          </motion.div>
        );

      case 'scale':
      default:
        if (typeof scale === 'number') {
          scale = {
            hover: scale,
            tap: scale
          };
        }
        return (
          <motion.div 
            ref={ref} 
            whileHover={{ scale: scale?.hover }} 
            whileTap={{ scale: scale?.tap }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            {children}
          </motion.div>
        );
    }
  }
);

export default AnimateButton;
